import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import axios from "axios";
import "./utilities/axios";
import "./index.css";
import "./App.css";
import DashboardPage from "./pages/dashboard/DashboardPage";
import CreateProject from "./pages/project/CreateProjectPage";
import ListProject from "./pages/project/ListProjects";
import ProjectSelection from "./pages/project/SelectionProject";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import Issues from "./pages/issues/IssuesPage";
import Work from "./pages/work/WorkPage";
import Provider from "./providers/Provider";
import AuthOutlet from "./providers/AuthOutlet";
import OrganizationUser from "./pages/organization-user/OrganizationUserPage";
import Neworganisation from "./pages/organization/NewOrganization";
import OrganizationList from "./pages/organization/OrganizationList";
import NotFoundPage from "./pages/404/NotFoundPage";
import ThemeContextProvider from "./providers/ThemeProvider";
import ReactAuthOutlet from "@auth-kit/react-router/AuthOutlet";
import InvitationPage from "./components/InvitationPage";
import TrialActivation from "./pages/TrialActivate";
import SignupTrial from "./pages/SignupTrial";
import { setCookie } from "./utilities/cookies";
import axiosInstance from "./utilities/axios";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import Loading from "./components/Loading";

const MinimalAuthOutlet = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const params = new URLSearchParams(search);
  const hasUserId = params.has("userId");
  const tokenParam = params.get("token");

  // Preserve original target and token when redirecting to register
  const redirectto = encodeURIComponent(`${pathname}${search || ""}`);
  const fallbackRegister = tokenParam
    ? `/signup?token=${encodeURIComponent(tokenParam)}&redirectto=${redirectto}`
    : `/signup?redirectto=${redirectto}`;

  return (
    <ThemeContextProvider>
      {hasUserId ? (
        <ReactAuthOutlet />
      ) : (
        <ReactAuthOutlet fallbackPath={fallbackRegister} />
      )}
    </ThemeContextProvider>
  );
};

const storedUser = JSON.parse(localStorage.getItem("user")) || null;

const AuthHandler = ({ setUser, setLoading }) => {
  const signIn = useSignIn();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get("userId");

    const handleUserLoginById = async (userIdParam) => {
      setLoading(true);
      try {
        const profileRes = await axios.post(
          "https://api.accounts.clikkle.com/api/auth/get_user_profile",
          { id: userIdParam }
        );

        const profileData = profileRes.data;

        if (!profileData?.user) return;

        setUser(profileData.user);
        setCookie("userId", profileData.user._id);
        setCookie("fullName", profileData.user.username);
        setCookie("role", profileData.user.role);

        try {
          const response = await axiosInstance.post("/api/v2/auth/login", {
            userName: profileData.user.username,
            recoveryEmail: profileData.user.recoveryEmail,
            firstName: profileData.user.firstName,
            lastName: profileData.user.lastName,
          });
          const { userDetails, token } = response.data;

          signIn({
            auth: { token },
            userState: userDetails,
          });
          localStorage.setItem("user", JSON.stringify(profileData.user));
          setLoading(false);
          // If returning to invite, do not force redirect away
          if (window.location.pathname === "/invite") {
            return;
          }
          // Respect an explicit redirectto param if present
          const redirectParam = new URLSearchParams(window.location.search).get(
            "redirectto"
          );
          if (redirectParam) {
            const absolute = redirectParam.startsWith("http")
              ? redirectParam
              : `${window.location.origin}${redirectParam}`;
            window.location.href = absolute;
          } else {
            window.location.href = "/listOrganization";
          }
        } catch (err) {
          console.log("Login error:", err);
        }
      } catch (err) {
        console.error("Error in handleUserLoginById:", err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      if (!storedUser) {
        handleUserLoginById(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

const App = () => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(storedUser);

  return (
    <Provider>
      {loading && (
        <Loading/>
      )}

      <AuthHandler setUser={setUser} setLoading={setLoading} />

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/register" element={<SignupTrial />} />
        <Route path="/register/trialactivate" element={<TrialActivation />} />

        <Route element={<MinimalAuthOutlet />}>
          <Route path="/invite" element={<InvitationPage />} />
          <Route path="/createOrganization" element={<Neworganisation />} />
          <Route path="/listOrganization" element={<OrganizationList />} />
          <Route path="/project-selection" element={<ProjectSelection />} />
          <Route path="/create-project" element={<CreateProject />} />
          <Route path="/list-project" element={<ListProject />} />
        </Route>

        <Route path="/" element={<AuthOutlet />}>
          <Route index element={<DashboardPage />} />
          <Route path="/organization-user" element={<OrganizationUser />} />
          <Route path="/view-project" element={<Issues />} />
          <Route path="/your-work" element={<Work />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Provider>
  );
};

export default App;
